import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS } from '../../assets/constants';
import { LegendConfig } from 'src/app/shared/components/map/map.component';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
} from 'src/app/shared/components/map/map.constants';
import { convertNumber } from 'src/app/shared/all.helpers';
import { PathOptions } from 'leaflet';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec8RedDark,
    colorAec9Cream,
    colorBlack,
} from 'src/app/shared/all.constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    ENEDIS_CUSTOMERS_ENERGY_USERS_MAP,
    ENEDIS_CUSTOMERS_EVOL_CONSUM_TOT_USERS_CHART,
    ENEDIS_CUSTOMERS_EVOL_NBR_LV_MV_CHART,
    ENEDIS_CUSTOMERS_KPIS,
    ENEDIS_CUSTOMERS_MEAN_CONSUMPTION_CHART,
    ENEDIS_CUSTOMERS_MEAN_KWH_PRICE_CHART,
    ENEDIS_CUSTOMERS_TRANSPORT_REVENUES_CHART,
} from '../../assets/keys';

const KEYS = [
    ENEDIS_CUSTOMERS_KPIS,
    ENEDIS_CUSTOMERS_ENERGY_USERS_MAP,
    ENEDIS_CUSTOMERS_EVOL_NBR_LV_MV_CHART,
    ENEDIS_CUSTOMERS_EVOL_CONSUM_TOT_USERS_CHART,
    ENEDIS_CUSTOMERS_TRANSPORT_REVENUES_CHART,
    ENEDIS_CUSTOMERS_MEAN_CONSUMPTION_CHART,
    ENEDIS_CUSTOMERS_MEAN_KWH_PRICE_CHART,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    usersCount?: number;
    usersCountEvolution?: number;

    totalConsumption?: number;
    totalConsumptionEvolution?: number;

    totalIncome?: number;
    totalIncomeEvolution?: number;
}

interface NbrUsersLvMvEvolutionChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    usersLvLess36?: number;
    usersLvGeater36?: number;
    usersMv?: number;
    total?: number;
    rateAnnualGrowth?: number;
    error?: boolean;
}

interface ConsuUsersEvolutionChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    usersConsuLvLess36?: number;
    usersConsuLvGeater36?: number;
    usersConsuMv?: number;
    totalConsu?: number;
    rateAnnualGrowthConsu?: number;
    error?: boolean;
}

interface MeanConsumptionChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    lvUsersUnder36?: number;
    lvUsersOver36?: number;
    mvUsers?: number;
}

interface TransportRevenuesChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    lvUsersUnder36?: number;
    lvUsersOver36?: number;
    mvUsers?: number;
    total?: number;
    totalEvolution?: number;
}

interface MeanKwhPriceChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    lvUsersUnder36?: number;
    lvUsersOver36?: number;
    mvUsers?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-enedis-users',
    templateUrl: './enedis-users.component.html',
    styleUrls: ['./enedis-users.component.scss'],
})
export class EnedisUsersComponent extends GenericComponent {
    public texts = CC_CONSTANTS.energyUsers.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_KPIS),
    ) as Signal<KpiData[]>;

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_ENERGY_USERS_MAP),
    ) as Signal<any[]>;

    public nbrLvMvUserChartError: boolean = true;
    public numberLvMvUserXaxisCategories: ApexXAxis['categories'];
    public nbrLvMvUserYaxis: ApexYAxis[] = [];
    public nbrLvMvUserDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null, 3: null }),
        style: this.setDataLabelsStyleColors(this.texts.numberLvMvUserEvolutionChart.totalTitle),
    };
    public nbrLvMvUserChartSeries: ApexAxisChartSeries = [];
    public nbrLvMvUserChartData: Signal<NbrUsersLvMvEvolutionChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_EVOL_NBR_LV_MV_CHART),
    ) as Signal<NbrUsersLvMvEvolutionChartData[]>;
    public nbrLvMvUserCustomTooltip: ApexTooltip['custom'];
    public nbrLvMvUserLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec3Green,
                colorBlack,
                colorAec8RedDark,
            ],
        },
    };

    public consUserChartError: boolean = true;
    public consUserYaxis: ApexYAxis[] = [];
    public consUserXaxisCategories: ApexXAxis['categories'];
    public consUserDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: this.texts.consumptionUserEvolutionChart.series[0].precision,
            1: this.texts.consumptionUserEvolutionChart.series[1].precision,
            2: this.texts.consumptionUserEvolutionChart.series[2].precision,
            3: this.texts.consumptionUserEvolutionChart.series[3].precision,
        }),
        style: this.setDataLabelsStyleColors(
            this.texts.consumptionUserEvolutionChart.series[3].name,
        ),
    };
    public consUserChartSeries: ApexAxisChartSeries = [];
    public consUserChartData: Signal<ConsuUsersEvolutionChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_EVOL_CONSUM_TOT_USERS_CHART),
    ) as Signal<ConsuUsersEvolutionChartData[]>;
    public consUserCustomTooltip: ApexTooltip['custom'];
    public consUserLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec3Green,
                colorBlack,
                colorAec8RedDark,
            ],
        },
    };

    public meanConsumptionChartSeries: ApexAxisChartSeries = [];
    public meanConsumptionDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: 1,
            1: null,
            2: null,
        }),
    };
    public meanConsumptionChartData: Signal<MeanConsumptionChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_MEAN_CONSUMPTION_CHART),
    ) as Signal<MeanConsumptionChartData[]>;
    public meanConsumptionYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            logarithmic: true,
            forceNiceScale: true,
            title: {
                text: this.texts.meanConsumptionChart.yaxisTitleLeft,
            },
        },
    ];
    public meanConsumptionChartError: boolean = true;

    public transportRevenuesChartSeries: ApexAxisChartSeries = [];
    public transportRevenuesXaxisCategories: ApexXAxis['categories'];
    public transportRevenuesDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null, 3: null }),
        style: this.setDataLabelsStyleColors(this.texts.transportRevenuesChart.totalTitle),
    };
    public transportRevenuesChartData: Signal<TransportRevenuesChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_TRANSPORT_REVENUES_CHART),
        ) as Signal<TransportRevenuesChartData[]>;
    public transportRevenuesYaxis: ApexYAxis[] = [];
    public transportRevenuesChartError: boolean = true;
    public transportRevenuesCustomTooltip: ApexTooltip['custom'];
    public transportRevenuesLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec3Green,
                colorBlack,
                colorAec8RedDark,
            ],
        },
    };

    public meanKwhPriceChartSeries: ApexAxisChartSeries = [];
    public meanKwhPriceDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public meanKwhPriceChartData: Signal<MeanKwhPriceChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CUSTOMERS_MEAN_KWH_PRICE_CHART),
    ) as Signal<MeanKwhPriceChartData[]>;
    public meanKwhPriceYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.meanKwhPriceChart.yaxisTitleLeft,
            },
        },
    ];
    public meanKwhPriceChartError: boolean = true;
    public meanKwhPriceGrid: ApexGrid = { padding: { left: 30 } };

    public legendConfig: LegendConfig[] = [
        {
            title: "Nombre d'usagers en soutirage (base clientèle)",
            values: [
                { color: colorAec9Cream, value: '0 à 500 usagers' },
                { color: colorAec6Gray, value: '500 à 2 000' },
                { color: colorAec3Green, value: '2 000 à 5 000' },
                { color: colorAec1BlueLight, value: '5 000 à 10 000' },
                { color: colorAec2BlueDark, value: 'Plus de 10 000 usagers' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);
        effect(() => {
            const nbrLvMvUserDataList = this.nbrLvMvUserChartData();
            this.nbrLvMvUserChartError = this.checkForError(nbrLvMvUserDataList, [
                'xYear',
                'usersLvLess36',
                'usersLvGeater36',
                'usersMv',
                'total',
                'rateAnnualGrowth',
            ]);
            if (!this.nbrLvMvUserChartError) {
                this.numberLvMvUserXaxisCategories = nbrLvMvUserDataList.map((data) => data.xYear!);
                const totales = this.getTotales(nbrLvMvUserDataList, [
                    'usersLvLess36',
                    'usersLvGeater36',
                    'usersMv',
                ]);

                const totalesMax = Math.round(totales.reduce((min, t) => Math.max(min, t), 0));
                this.nbrLvMvUserYaxis = [
                    {
                        seriesName: [
                            this.texts.numberLvMvUserEvolutionChart.usersMvAxisTitle,
                            this.texts.numberLvMvUserEvolutionChart.usersLvGeater36AxisTitle,
                            this.texts.numberLvMvUserEvolutionChart.usersLvLess36AxisTitle,
                            this.texts.numberLvMvUserEvolutionChart.totalTitle,
                        ],
                        min: 0,
                        max: Math.round(totalesMax / 100 + 1) * 100,
                        forceNiceScale: true,
                        tickAmount: 4,
                        title: {
                            text: this.texts.numberLvMvUserEvolutionChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName: [
                            this.texts.numberLvMvUserEvolutionChart.rateAnnualGrowthAxisTitle,
                        ],
                        min: 0,
                        max: Math.round(
                            nbrLvMvUserDataList.reduce(
                                (max, t) => Math.max(max, t.rateAnnualGrowth ?? 0),
                                0,
                            ),
                        ),
                        tickAmount: 5,
                        opposite: true,
                        forceNiceScale: true,
                        labels: {
                            formatter: (value: number) => convertNumber(value, null),
                        },
                    },
                ];
                this.nbrLvMvUserChartSeries = [
                    {
                        name: this.texts.numberLvMvUserEvolutionChart.usersLvLess36AxisTitle,
                        type: 'column',
                        data: nbrLvMvUserDataList.map((data) => data.usersLvLess36!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.numberLvMvUserEvolutionChart.usersLvGeater36AxisTitle,
                        type: 'column',
                        data: nbrLvMvUserDataList.map((data) => data.usersLvGeater36!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.numberLvMvUserEvolutionChart.usersMvAxisTitle,
                        type: 'column',
                        data: nbrLvMvUserDataList.map((data) => data.usersMv!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.numberLvMvUserEvolutionChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                    {
                        name: this.texts.numberLvMvUserEvolutionChart.rateAnnualGrowthAxisTitle,
                        type: 'line',
                        data: nbrLvMvUserDataList.map((data) => data.rateAnnualGrowth!),
                        color: colorAec8RedDark,
                    },
                ];
                this.nbrLvMvUserDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const consu = this.consUserChartData();
            this.consUserChartError = this.checkForError(consu, [
                'xYear',
                'usersConsuLvLess36',
                'usersConsuLvGeater36',
                'usersConsuMv',
                'rateAnnualGrowthConsu',
            ]);
            if (!this.consUserChartError) {
                this.consUserXaxisCategories = consu.map((data) => data.xYear);
                const totales = this.getTotales(consu, [
                    'usersConsuLvLess36',
                    'usersConsuLvGeater36',
                    'usersConsuMv',
                ]);
                const totalesMax = totales.reduce((max, t) => Math.max(max, t), 0);
                const rateMin = consu.reduce(
                    (min, t) => Math.min(min, t.rateAnnualGrowthConsu ?? 0),
                    Infinity,
                );

                const rateMax = consu.reduce(
                    (max, t) => Math.max(max, t.rateAnnualGrowthConsu ?? 0),
                    -Infinity,
                );
                this.consUserYaxis = [
                    {
                        seriesName: [
                            this.texts.consumptionUserEvolutionChart.series[0].name,
                            this.texts.consumptionUserEvolutionChart.series[1].name,
                            this.texts.consumptionUserEvolutionChart.series[2].name,
                            this.texts.consumptionUserEvolutionChart.series[3].name,
                        ],
                        forceNiceScale: true,
                        min: 0,
                        max: Math.round(totalesMax / 100 + 1) * 100,
                        tickAmount: 4,
                        decimalsInFloat: 0,
                        title: {
                            text: this.texts.consumptionUserEvolutionChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName: this.texts.consumptionUserEvolutionChart.series[4].name,
                        opposite: true,
                        forceNiceScale: true,
                        tickAmount: 4,
                        decimalsInFloat: 1,
                        min: Math.floor(rateMin) - (Math.floor(rateMin) % 2),
                        max: Math.ceil(rateMax) + (Math.ceil(rateMax) % 2),
                        labels: {
                            formatter: (value: number) => convertNumber(value, null),
                        },
                    },
                ];
                this.consUserChartSeries = [
                    {
                        name: this.texts.consumptionUserEvolutionChart.series[0].name,
                        type: 'column',
                        data: consu.map((data) => data.usersConsuLvLess36!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.consumptionUserEvolutionChart.series[1].name,
                        type: 'column',
                        data: consu.map((data) => data.usersConsuLvGeater36!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.consumptionUserEvolutionChart.series[2].name,
                        type: 'column',
                        data: consu.map((data) => data.usersConsuMv!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.consumptionUserEvolutionChart.series[3].name,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                    {
                        name: this.texts.consumptionUserEvolutionChart.series[4].name,
                        type: 'line',
                        data: consu.map((data) => data.rateAnnualGrowthConsu!),
                        color: colorAec8RedDark,
                    },
                ];
                this.consUserDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const meanConsumptionDataList = this.meanConsumptionChartData();
            this.meanConsumptionChartError = this.checkForError(meanConsumptionDataList, [
                'xYear',
                'lvUsersUnder36',
                'lvUsersOver36',
                'mvUsers',
            ]);

            if (!this.meanConsumptionChartError) {
                this.meanConsumptionChartSeries = [
                    {
                        name: this.texts.meanConsumptionChart.lvUsersUnder36Title,
                        type: 'line',
                        data: meanConsumptionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.lvUsersUnder36,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.meanConsumptionChart.lvUsersOver36Title,
                        type: 'line',
                        data: meanConsumptionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.lvUsersOver36,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.meanConsumptionChart.mvUsersTitle,
                        type: 'line',
                        data: meanConsumptionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.mvUsers,
                        })),
                        color: colorAec3Green,
                    },
                ];
                this.meanConsumptionDataLabels.enabledOnSeries = [0, 1, 2];
            }

            const transportRevenuesDataList = this.transportRevenuesChartData();
            this.transportRevenuesChartError = this.checkForError(transportRevenuesDataList, [
                'xYear',
                'lvUsersUnder36',
                'lvUsersOver36',
                'mvUsers',
                'total',
                'totalEvolution',
            ]);

            if (!this.transportRevenuesChartError) {
                this.transportRevenuesXaxisCategories = transportRevenuesDataList.map(
                    (data) => data.xYear,
                );

                const totalMax = transportRevenuesDataList.reduce(
                    (max, data) => Math.max(max, data.total ?? 0),
                    0,
                );
                const totalEvolutionMax = transportRevenuesDataList.reduce(
                    (max, data) => Math.max(max, data.totalEvolution ?? 0),
                    0,
                );

                this.transportRevenuesYaxis = [
                    {
                        seriesName: [
                            this.texts.transportRevenuesChart.lvUsersUnder36Title,
                            this.texts.transportRevenuesChart.lvUsersOver36Title,
                            this.texts.transportRevenuesChart.mvUsersTitle,
                            this.texts.transportRevenuesChart.totalTitle,
                        ],
                        min: 0,
                        max: Math.ceil(totalMax / 40 + 1) * 40,
                        forceNiceScale: true,
                        decimalsInFloat: 0,
                        tickAmount: 4,
                        title: {
                            text: this.texts.transportRevenuesChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName: this.texts.transportRevenuesChart.totalEvolutionTitle,
                        opposite: true,
                        decimalsInFloat: 0,
                        tickAmount: 4,
                        min: 0,
                        max: Math.ceil(totalEvolutionMax) + (Math.ceil(totalEvolutionMax) % 2),
                    },
                ];

                this.transportRevenuesChartSeries = [
                    {
                        name: this.texts.transportRevenuesChart.lvUsersUnder36Title,
                        type: 'column',
                        data: transportRevenuesDataList.map((data) => data.lvUsersUnder36!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.transportRevenuesChart.lvUsersOver36Title,
                        type: 'column',
                        data: transportRevenuesDataList.map((data) => data.lvUsersOver36!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.transportRevenuesChart.mvUsersTitle,
                        type: 'column',
                        data: transportRevenuesDataList.map((data) => data.mvUsers!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.transportRevenuesChart.totalTitle,
                        type: 'line',
                        data: transportRevenuesDataList.map((data) => data.total!),
                        color: 'transparent',
                    },
                    {
                        name: this.texts.transportRevenuesChart.totalEvolutionTitle,
                        type: 'line',
                        data: transportRevenuesDataList.map((data) => data.totalEvolution!),
                        color: colorAec8RedDark,
                    },
                ];
                this.transportRevenuesDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const meanKwhPriceDataList = this.meanKwhPriceChartData();
            this.meanKwhPriceChartError = this.checkForError(meanKwhPriceDataList, [
                'xYear',
                'lvUsersUnder36',
                'lvUsersOver36',
                'mvUsers',
            ]);

            if (!this.meanKwhPriceChartError) {
                this.meanKwhPriceChartSeries = [
                    {
                        name: this.texts.meanKwhPriceChart.lvUsersUnder36Title,
                        type: 'line',
                        data: meanKwhPriceDataList.map((data) => ({
                            x: data.xYear,
                            y: data.lvUsersUnder36,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.meanKwhPriceChart.lvUsersOver36Title,
                        type: 'line',
                        data: meanKwhPriceDataList.map((data) => ({
                            x: data.xYear,
                            y: data.lvUsersOver36,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.meanKwhPriceChart.mvUsersTitle,
                        type: 'line',
                        data: meanKwhPriceDataList.map((data) => ({
                            x: data.xYear,
                            y: data.mvUsers,
                        })),
                        color: colorAec3Green,
                    },
                ];
                this.meanKwhPriceDataLabels.enabledOnSeries = [0, 1, 2];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.nbrLvMvUserCustomTooltip = this.setNbrLvMvUserCustomTooltip();
        this.consUserCustomTooltip = this.setConsUserCustomTooltip();
        this.transportRevenuesCustomTooltip = this.setTransportRevenuesCustomTooltip();
    }

    private setNbrLvMvUserCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.numberLvMvUserEvolutionChart.precision;
            const lvUnder36Serie = w.globals.initialSeries[0];
            const {
                name: lvUnder36Name,
                color: lvUnder36Color,
                valueStringified: lvUnder36Stringified,
            } = this.setCustomTooltipData(precision, lvUnder36Serie, dataPointIndex);

            const lvOver36Serie = w.globals.initialSeries[1];
            const {
                name: lvOver36Name,
                color: lvOver36Color,
                valueStringified: lvOver36Stringified,
            } = this.setCustomTooltipData(precision, lvOver36Serie, dataPointIndex);

            const mvSerie = w.globals.initialSeries[2];
            const {
                name: mvName,
                color: mvColor,
                valueStringified: mvStringified,
            } = this.setCustomTooltipData(precision, mvSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const ratePrecision = this.texts.numberLvMvUserEvolutionChart.ratePrecision;
            const rateSerie = w.globals.initialSeries[4];
            const {
                name: rateName,
                color: rateColor,
                valueStringified: rateStringified,
            } = this.setCustomTooltipData(ratePrecision, rateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: lvUnder36Color,
                    name: lvUnder36Name,
                    value: lvUnder36Stringified,
                },
                {
                    color: lvOver36Color,
                    name: lvOver36Name,
                    value: lvOver36Stringified,
                },
                {
                    color: mvColor,
                    name: mvName,
                    value: mvStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setConsUserCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const lvUnder36Precision = this.texts.consumptionUserEvolutionChart.series[0].precision;
            const lvUnder36Serie = w.globals.initialSeries[0];
            const {
                name: lvUnder36Name,
                color: lvUnder36Color,
                valueStringified: lvUnder36Stringified,
            } = this.setCustomTooltipData(lvUnder36Precision, lvUnder36Serie, dataPointIndex);

            const lvOver36Precision = this.texts.consumptionUserEvolutionChart.series[1].precision;
            const lvOver36Serie = w.globals.initialSeries[1];
            const {
                name: lvOver36Name,
                color: lvOver36Color,
                valueStringified: lvOver36Stringified,
            } = this.setCustomTooltipData(lvOver36Precision, lvOver36Serie, dataPointIndex);

            const mvPrecision = this.texts.consumptionUserEvolutionChart.series[2].precision;
            const mvSerie = w.globals.initialSeries[2];
            const {
                name: mvName,
                color: mvColor,
                valueStringified: mvStringified,
            } = this.setCustomTooltipData(mvPrecision, mvSerie, dataPointIndex);

            const totalPrecision = this.texts.consumptionUserEvolutionChart.series[3].precision;
            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(totalPrecision, totalesSerie, dataPointIndex);

            const ratePrecision = this.texts.consumptionUserEvolutionChart.series[4].precision;
            const rateSerie = w.globals.initialSeries[4];
            const {
                name: rateName,
                color: rateColor,
                valueStringified: rateStringified,
            } = this.setCustomTooltipData(ratePrecision, rateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: lvUnder36Color,
                    name: lvUnder36Name,
                    value: lvUnder36Stringified + ' GWh',
                },
                {
                    color: lvOver36Color,
                    name: lvOver36Name,
                    value: lvOver36Stringified + ' GWh',
                },
                {
                    color: mvColor,
                    name: mvName,
                    value: mvStringified + ' GWh',
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified + ' GWh',
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setTransportRevenuesCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = CC_CONSTANTS.energyUsers.texts.transportRevenuesChart.precision;
            const incomeLvUnder36Serie = w.globals.initialSeries[0];
            const {
                name: incomeLvUnder36Name,
                color: incomeLvUnder36Color,
                valueStringified: incomeLvUnder36Stringified,
            } = this.setCustomTooltipData(precision, incomeLvUnder36Serie, dataPointIndex);

            const incomeLvOver36Serie = w.globals.initialSeries[1];
            const {
                name: incomeLvOver36Name,
                color: incomeLvOver36Color,
                valueStringified: incomeLvOver36Stringified,
            } = this.setCustomTooltipData(precision, incomeLvOver36Serie, dataPointIndex);

            const incomeMvSerie = w.globals.initialSeries[2];

            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);
            const {
                name: incomeMvName,
                color: incomeMvColor,
                valueStringified: incomeMvStringified,
            } = this.setCustomTooltipData(precision, incomeMvSerie, dataPointIndex);

            const ratePrecision =
                CC_CONSTANTS.energyUsers.texts.transportRevenuesChart.ratePrecision;
            const incomeRateSerie = w.globals.initialSeries[4];
            const {
                name: incomeRateName,
                color: incomeRateColor,
                valueStringified: incomeRateStringified,
            } = this.setCustomTooltipData(ratePrecision, incomeRateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: incomeLvUnder36Color,
                    name: incomeLvUnder36Name,
                    value: incomeLvUnder36Stringified,
                },
                {
                    color: incomeLvOver36Color,
                    name: incomeLvOver36Name,
                    value: incomeLvOver36Stringified,
                },
                {
                    color: incomeMvColor,
                    name: incomeMvName,
                    value: incomeMvStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
                {
                    color: incomeRateColor,
                    name: incomeRateName,
                    value: incomeRateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        if (!feature?.properties || feature.properties['usersCount'] === null) {
            return EmptyPolygonStyle;
        }

        // Cities style
        let fillColor = colorAec9Cream;
        const usersCount = Number(feature.properties['usersCount']);
        if (usersCount > 10000) {
            fillColor = colorAec2BlueDark;
        } else if (usersCount > 5000) {
            fillColor = colorAec1BlueLight;
        } else if (usersCount > 2000) {
            fillColor = colorAec3Green;
        } else if (usersCount > 500) {
            fillColor = colorAec6Gray;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        const {
            cityName,
            isInConcession,
            usersCount,
            usersCountEvo,
            totalConsumption,
            totalConsumptionEvo,
            income,
            incomeEvo,
        } = feature.properties;
        if (!isInConcession) {
            return `${cityName} - Hors concession`;
        }

        const stringifiedUsersCount = convertNumber(Number(usersCount), null);
        const usersCountEvoPercentage = usersCountEvo
            ? convertNumber(Number(usersCountEvo), null, true) + ' %'
            : '';
        const stringifiedUsersCountEvo = usersCountEvoPercentage
            ? ' (' + usersCountEvoPercentage + ')'
            : '';

        const stringifiedConsumption = convertNumber(Number(totalConsumption), null);
        const consumptionPercentage = totalConsumptionEvo
            ? convertNumber(Number(totalConsumptionEvo), null, true) + ' %'
            : '';
        const stringifiedConsumptionEvo = consumptionPercentage
            ? ' (' + consumptionPercentage + ')'
            : '';
        const stringifiedIncome = convertNumber(Number(income), null);
        const incomePercentage = incomeEvo
            ? convertNumber(Number(incomeEvo), null, true) + ' %'
            : '';
        const stringifiedIncomeEvo = incomePercentage ? ' (' + incomePercentage + ')' : '';

        return `
          ${cityName}<br/>
          ${stringifiedUsersCount} usagers en soutirage ${stringifiedUsersCountEvo}<br/>
          Consommation totale des usagers en soutirage : ${stringifiedConsumption} MWh ${stringifiedConsumptionEvo}<br/>
          Recette d'acheminement totale des usagers en soutirage : ${stringifiedIncome} k€ ${stringifiedIncomeEvo}<br/>
        `;
    }
}
